import React, { useEffect, useMemo, useState } from "react";
import type { ApiClient } from "../../types/UserTypes";
import type { FormattedInvoicePayload, ReportAssetV3 } from "../formattedInvoice/formattedInvoiceData";
import { isNativeReportsV31Payload, resolveNativeLayoutPlan, type NativeResolvedElement, type NativeLayoutPlan } from "./nativeLayout.mjs";
import NativeReportImage from "./NativeReportImage";
import { assertReportAdmission, reportAdmission } from "./nativeConditions.mjs";
import { NativeConditionError } from "./nativeConditions.mjs";
import type { InternalVisibilityExecution } from "./nativeVisibility.mjs";
import { getReportIncludeLabel } from "./reportInclude.mjs";
import styles from "./ReportsV3Preview.module.css";

type ReportsV3PreviewProps = {
  api: ApiClient | null;
  payload: FormattedInvoicePayload;
};

type LayoutSection = Record<string, any>;
type LayoutStyle = Record<string, any>;
type TableColumn = Record<string, any> & {
  key?: string;
  label?: string;
  format?: string;
  colSpan?: number;
  bodyColSpan?: number;
  wrap?: boolean;
  width?: number;
  align?: React.CSSProperties["textAlign"];
  horizontalAlign?: React.CSSProperties["textAlign"];
  headerHorizontalAlign?: React.CSSProperties["textAlign"];
};

type SummaryBadge = {
  status: string;
  count: string | number;
  label: string;
};

export function hasReportsV3PreviewData(payload: FormattedInvoicePayload): boolean {
  assertReportAdmission(payload);
  if (isNativeReportsV31Payload(payload)) {
    return resolveNativeLayoutPlan(payload).pages.some((page) => page.bands.some((band) => band.elements.some(nativeElementHasValue)));
  }
  const sections = previewSections(payload);
  return sections.some((section) => {
    if (section.type === "repeat") return repeatRows(payload, section).some((row) => {
      const scopedPayload = createScopedPayload(payload, row);
      return repeatSections(section)
        .filter((child) => Array.isArray(child.outputs) && child.outputs.includes("preview"))
        .some((child) => hasSectionPreviewData(scopedPayload, child));
    });
    return hasSectionPreviewData(payload, section);
  });
}

function hasSectionPreviewData(payload: FormattedInvoicePayload, section: LayoutSection): boolean {
  if (section.type === "table") return tableRows(payload, section).length > 0;
  if (section.type === "customFields") return customFieldRows(payload, section).length > 0;
  if (section.type === "keyValueGrid") {
    const dataset = getDataset(payload, section.dataset);
    return (section.fields || []).some((field: any) => getPath(dataset, field.key) != null);
  }
  if (section.type === "text") return Boolean(resolveTemplate(payload, section.content || "").trim());
  return section.type === "image" || section.type === "range" || section.type === "signatureBlock";
}

export default function ReportsV3Preview({ api, payload }: ReportsV3PreviewProps) {
  const admission = reportAdmission(payload);
  if (!admission.admitted) return <div role="alert" data-error={admission.error.code}>{admission.error.message}</div>;
  try { getReportIncludeLabel(payload.metadata); }
  catch (error) {
    if (!(error instanceof Error) || !("code" in error) || error.code !== "REPORT_INCLUDE_INVALID") throw error;
    return <div role="alert" data-error={error.code}>{error.message}</div>;
  }
  return isNativeReportsV31Payload(payload)
    ? <NativeReportsV31Preview payload={payload} />
    : <LegacyReportsV3Preview api={api} payload={payload} />;
}

function LegacyReportsV3Preview({ api, payload }: ReportsV3PreviewProps) {
  const includeLabel = getReportIncludeLabel(payload.metadata);
  const sections = useMemo(() => previewSections(payload), [payload]);
  const layout = asRecord(payload.layout);
  const layoutStyles = asRecord(layout.styles);
  const [assetUrls, setAssetUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let disposed = false;
    const objectUrls: string[] = [];

    async function loadAssets() {
      const entries = await Promise.all(assetSections(sections)
        .filter((section) => section.type === "image" || section.type === "companyHeader")
        .map(async (section) => {
          const asset = resolveAsset(payload, section.asset);
          if (!asset?.available || !asset.url) return null;
          if (isExternalAssetUrl(asset.url)) return [section.id || section.asset, asset.url] as const;
          if (!api) return null;
          try {
            const blob = await api.getBlob(normalizeAssetPath(asset.url));
            const objectUrl = URL.createObjectURL(blob);
            objectUrls.push(objectUrl);
            return [section.id || section.asset, objectUrl] as const;
          } catch {
            return null;
          }
        }));

      if (!disposed) {
        setAssetUrls(Object.fromEntries(entries.filter(Boolean) as Array<readonly [string, string]>));
      }
    }

    void loadAssets();

    return () => {
      disposed = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [api, payload, sections]);

  return (
    <article className={styles["reports-v3-preview"]} data-report-root data-template-id={payload.definitionId || undefined}>
      {includeLabel && <div className={styles["reports-v3-include"]}>{includeLabel}</div>}
      {sections.map((section) => (
        <ReportsV3Section
          key={section.id || `${section.type}-${sections.indexOf(section)}`}
          section={section}
          payload={payload}
          layoutStyles={layoutStyles}
          assetUrl={assetUrls[section.id || section.asset]}
          assetUrls={assetUrls}
        />
      ))}
    </article>
  );
}

export type NativePreviewResult = { plan: NativeLayoutPlan } | { error: NativeConditionError };

export function NativeReportsV31Preview({ payload, visibilityExecution, typedExecution, onResult }: {
  payload: FormattedInvoicePayload; visibilityExecution?: InternalVisibilityExecution; typedExecution?: import("./nativeTypedExecution.mjs").InternalTypedExecution; onResult?: (result: NativePreviewResult) => void;
}) {
  const result = useMemo<{ plan: NativeLayoutPlan } | { error: NativeConditionError }>(() => {
    try {
      const includeLabel = getReportIncludeLabel(payload.metadata);
      const plan = resolveNativeLayoutPlan(payload, { visibilityExecution, typedExecution });
      if (includeLabel && (plan.page.margins.top < 18 || plan.page.printableArea.width < 180)) {
        return { error: new NativeConditionError("REPORT_INCLUDE_SPACE", "$.layout.page",
          "Report Include context requires a top margin of at least 18pt and content width of at least 180pt. Authored layout was not changed.") };
      }
      return { plan };
    }
    catch (error) {
      if (error instanceof Error && "code" in error && error.code === "REPORT_INCLUDE_INVALID") {
        return { error: new NativeConditionError(error.code, "$.metadata.invoicedOnly", error.message) };
      }
      if (error instanceof Error && "code" in error && error.code === "UNSUPPORTED_NATIVE_WORKBOOK") {
        return { error: new NativeConditionError("UNSUPPORTED_REPORT_PROFILE", "$.layout", error.message) };
      }
      if (!(error instanceof NativeConditionError)) throw error;
      return { error };
    }
  }, [payload, visibilityExecution, typedExecution]);
  useEffect(() => { onResult?.(result); }, [result, onResult]);
  if ("error" in result) return <div role="alert" data-error={result.error.code}>{result.error.message}</div>;
  const plan = result.plan;
  const includeLabel = getReportIncludeLabel(payload.metadata);
  const scale = 96 / 72;
  return (
    <article className={styles["reports-v3-native-preview"]} data-report-root data-template-id={payload.definitionId || undefined}>
      {plan.visibility?.notices.map((notice) => <div role="status" key={notice}>{notice}</div>)}
      {plan.overflow.length > 0 && (
        <div className={styles["reports-v3-native-warning"]}>
          {plan.overflow.length} positioned element{plan.overflow.length === 1 ? "" : "s"} overflow their configured height.
        </div>
      )}
      {plan.pages.map((page) => (
        <section
          className={styles["reports-v3-native-page"]}
          data-testid={`reports-v3-native-page-${page.pageNumber}`}
          key={page.pageNumber}
          style={{
            width: `${page.width * scale}px`,
            height: `${page.height * scale}px`,
          }}
        >
          {includeLabel && (
            <div className={styles["reports-v3-native-include"]} style={{
              left: `${plan.page.margins.left * scale}px`,
              top: `${(plan.page.margins.top - 14) * scale}px`,
              width: `${plan.page.printableArea.width * scale}px`,
            }}>{includeLabel}</div>
          )}
          {page.bands.map((band) => (
            <div
              className={`${styles["reports-v3-native-band"]} ${styles[`reports-v3-native-band--${band.role}`] || ""}`}
              data-testid={`reports-v3-native-band-${band.id}-page-${page.pageNumber}`}
              key={`${band.id}-${band.pageNumber}-${band.rowIndex ?? "fixed"}`}
              style={{
                left: `${band.bounds.x * scale}px`,
                top: `${band.bounds.y * scale}px`,
                width: `${band.bounds.width * scale}px`,
                height: `${band.bounds.height * scale}px`,
              }}
            >
              {band.elements.map((element) => (
                <NativeElementView element={element} key={element.id} payload={payload} scale={scale} />
              ))}
            </div>
          ))}
        </section>
      ))}
    </article>
  );
}

function NativeElementView({ element, payload, scale }: { element: NativeResolvedElement; payload: FormattedInvoicePayload; scale: number }) {
  const style = element.style || {};
  if (element.type === "image") {
    const assetKey = (element as unknown as Record<string, unknown>).asset as string | undefined;
    const fit = ((element as unknown as Record<string, unknown>).fit || "contain") as string;
    return (
      <div
        className={`${styles["reports-v3-native-element"]} ${styles["reports-v3-native-image"] || ""}`}
        data-testid={`reports-v3-native-element-${element.id}`}
        style={{
          left: `${element.x * scale}px`,
          top: `${element.y * scale}px`,
          width: `${element.width * scale}px`,
          height: `${element.height * scale}px`,
          backgroundColor: style.backgroundColor,
          borderColor: style.borderColor,
          borderWidth: element.conditionalStyle ? (style.borderWidth ?? (style.borderColor ? 1 : 0)) * scale : style.borderColor || style.borderWidth ? `${Number(style.borderWidth || 1) * scale}px` : undefined,
          borderStyle: element.conditionalStyle ? "solid" : undefined,
        }}
      >
        <NativeReportImage payload={payload} reference={assetKey} fit={fit} />
      </div>
    );
  }
  if (element.type === "table" && element.table) {
    return (
      <div
        className={styles["reports-v3-native-table"]}
        data-testid={`reports-v3-native-element-${element.id}`}
        style={{
          left: `${element.x * scale}px`,
          top: `${element.y * scale}px`,
          width: `${element.width * scale}px`,
          height: `${element.height * scale}px`,
          color: style.color || "#000",
          fontSize: `${(style.fontSize || 10) * scale}px`,
        }}
      >
        {element.table.header && <NativeTableRowView columns={element.table.columns} row={element.table.header} scale={scale} tableStyle={style} />}
        {element.table.rows.map((row) => (
          <NativeTableRowView columns={element.table!.columns} key={`${row.kind}-${row.rowIndex}-${row.y}`} row={row} scale={scale} tableStyle={style} />
        ))}
        {element.table.footer && <NativeTableRowView columns={element.table.columns} row={element.table.footer} scale={scale} tableStyle={style} />}
      </div>
    );
  }
  if (element.type === "container") {
    return (
      <div
        className={`${styles["reports-v3-native-container"]} ${element.overflow ? styles["reports-v3-native-element--overflow"] : ""}`}
        data-testid={`reports-v3-native-element-${element.id}`}
        title={element.overflow ? "Container content exceeds its bounds." : undefined}
        style={{
          left: `${element.x * scale}px`,
          top: `${element.y * scale}px`,
          width: `${element.width * scale}px`,
          height: `${element.height * scale}px`,
          backgroundColor: style.backgroundColor,
          borderColor: style.borderColor,
          borderWidth: element.conditionalStyle ? (style.borderWidth ?? (style.borderColor ? 1 : 0)) * scale : style.borderColor || style.borderWidth ? `${Number(style.borderWidth || 1) * scale}px` : undefined,
        }}
      >
        {(element.elements || []).map((child) => (
          <NativeElementView element={child} key={child.id} payload={payload} scale={scale} />
        ))}
      </div>
    );
  }
  return (
    <div
      className={`${styles["reports-v3-native-element"]} ${element.overflow ? styles["reports-v3-native-element--overflow"] : ""}`}
      data-testid={`reports-v3-native-element-${element.id}`}
      title={element.overflow ? "Text exceeds element height." : undefined}
      style={{
        left: `${element.x * scale}px`,
        top: `${element.y * scale}px`,
        width: `${element.width * scale}px`,
        height: `${element.height * scale}px`,
        color: style.color || "#000",
        backgroundColor: style.backgroundColor,
        border: element.conditionalStyle ? `${(style.borderWidth ?? (style.borderColor ? 1 : 0)) * scale}px solid ${style.borderColor || "#000000"}` : undefined,
        fontSize: `${(style.fontSize || 10) * scale}px`,
        fontWeight: style.bold === false ? 400 : style.bold ? 700 : 400,
        fontStyle: style.italic ? "italic" : undefined,
        lineHeight: style.lineHeight || 1.2,
        textAlign: style.horizontalAlign || "left",
        alignItems: style.verticalAlign === "middle" ? "center" : style.verticalAlign === "bottom" ? "flex-end" : "flex-start",
        ...(element.conditionalStyle ? { flexDirection: "column", alignItems: "stretch", justifyContent: style.verticalAlign === "middle" ? "center" : style.verticalAlign === "bottom" ? "flex-end" : "flex-start" } : {}),
      }}
    >
      {element.value}
    </div>
  );
}

function NativeTableRowView({
  columns,
  row,
  scale,
  tableStyle,
}: {
  columns: NonNullable<NativeResolvedElement["table"]>["columns"];
  row: NonNullable<NonNullable<NativeResolvedElement["table"]>["header"]>;
  scale: number;
  tableStyle: Record<string, any>;
}) {
  const isHeader = row.kind === "header" || row.kind === "tableHeader";
  const isFooter = row.kind === "footer" || row.kind === "tableFooter" || row.kind === "groupFooter";
  const isGroupHeader = row.kind === "groupHeader";
  return (
    <>
      {row.cells.map((cell, index) => {
        if (cell.reportVisible === false) return null;
        const matchedColumn = columns.find((candidate) => candidate.id === cell.columnId);
        if (cell.columnId && !matchedColumn) return null;
        const column = matchedColumn || columns[index];
        const style = cell.style || {};
        return (
          <div
            className={`${styles["reports-v3-native-table-cell"]} ${isHeader || isGroupHeader ? styles["reports-v3-native-table-cell--header"] : ""} ${isFooter ? styles["reports-v3-native-table-cell--footer"] : ""}`}
            data-testid={`reports-v3-native-table-cell-${cell.columnId}-${row.kind}-${row.rowIndex ?? "fixed"}`}
            key={`${cell.columnId}-${index}`}
            style={{
              left: `${cell.x * scale}px`,
              top: `${cell.y * scale}px`,
              width: `${cell.width * scale}px`,
              height: `${cell.height * scale}px`,
              padding: `${cell.padding.top * scale}px ${cell.padding.right * scale}px ${cell.padding.bottom * scale}px ${cell.padding.left * scale}px`,
              backgroundColor: cell.conditionalStyle ? style.backgroundColor : isHeader || isGroupHeader
                ? tableStyle.headerBackgroundColor || "#f0f5ff"
                : isFooter
                  ? tableStyle.footerBackgroundColor || "#f8fbff"
                  : style.backgroundColor,
              borderColor: cell.conditionalStyle ? style.borderColor || "#808080" : tableStyle.borderColor || "#808080",
              borderWidth: cell.conditionalStyle ? (style.borderWidth ?? 0.5) * scale : undefined,
              color: style.color || tableStyle.color || "#000",
              fontSize: `${(style.fontSize || tableStyle.fontSize || 10) * scale}px`,
              fontWeight: cell.conditionalStyle ? style.bold ? 700 : 400 : isHeader || isFooter || isGroupHeader || style.bold ? 700 : 400,
              fontStyle: cell.conditionalStyle ? style.italic ? "italic" : "normal" : undefined,
              lineHeight: style.lineHeight || tableStyle.lineHeight || 1.25,
              textAlign: style.horizontalAlign || column?.horizontalAlign || (cell.format === "money" || cell.format === "number" ? "right" : "left"),
              ...(cell.conditionalStyle ? { display: "flex", flexDirection: "column", justifyContent: style.verticalAlign === "middle" ? "center" : style.verticalAlign === "bottom" ? "flex-end" : "flex-start" } : {}),
            }}
          >
            {cell.value}
          </div>
        );
      })}
    </>
  );
}

function nativeElementHasValue(element: NativeResolvedElement): boolean {
  if (element.type === "container") return (element.elements || []).some(nativeElementHasValue);
  if (element.type === "table") return Boolean(element.table?.rows.length || element.table?.footer);
  return Boolean(String(element.value || "").trim());
}

function ReportsV3Section({
  section,
  payload,
  layoutStyles,
  assetUrl,
  assetUrls = {},
}: {
  section: LayoutSection;
  payload: FormattedInvoicePayload;
  layoutStyles: Record<string, unknown>;
  assetUrl?: string;
  assetUrls?: Record<string, string>;
}) {
  switch (section.type) {
    case "companyHeader":
      return <CompanyHeaderSection section={section} payload={payload} assetUrl={assetUrl} />;
    case "image":
      return <ImageSection section={section} payload={payload} assetUrl={assetUrl} />;
    case "range":
      return <DividerSection section={section} layoutStyles={layoutStyles} />;
    case "keyValueGrid":
      return <KeyValueGridSection section={section} payload={payload} layoutStyles={layoutStyles} />;
    case "text":
      return <TextSection section={section} payload={payload} layoutStyles={layoutStyles} />;
    case "table":
      return <TableSection section={section} payload={payload} layoutStyles={layoutStyles} />;
    case "customFields":
      return <CustomFieldsSection section={section} payload={payload} layoutStyles={layoutStyles} />;
    case "repeat":
      return <RepeatSection section={section} payload={payload} layoutStyles={layoutStyles} assetUrls={assetUrls} />;
    case "signatureBlock":
      return <SignatureSection section={section} layoutStyles={layoutStyles} />;
    default:
      return null;
  }
}

function RepeatSection({
  section,
  payload,
  layoutStyles,
  assetUrls,
}: {
  section: LayoutSection;
  payload: FormattedInvoicePayload;
  layoutStyles: Record<string, unknown>;
  assetUrls: Record<string, string>;
}) {
  const rows = repeatRows(payload, section);
  const sections = repeatSections(section).filter((child) => Array.isArray(child.outputs) && child.outputs.includes("preview"));
  if (rows.length === 0 || sections.length === 0) return null;
  return (
    <>
      {rows.map((row, rowIndex) => {
        const scopedPayload = createScopedPayload(payload, row);
        return (
          <section
            key={`${section.id || "repeat"}-${rowIndex}`}
            className={`${styles["reports-v3-preview-repeat"]} ${rowIndex > 0 ? styles["reports-v3-preview-repeat--page-break"] : ""}`}
          >
            {sections
              .filter((child) => !isDuplicateSummaryCustomFields(scopedPayload, child))
              .map((child, childIndex) => (
                <ReportsV3Section
                  key={child.id || `${child.type}-${childIndex}`}
                  section={child}
                  payload={scopedPayload}
                  layoutStyles={layoutStyles}
                  assetUrl={assetUrls[child.id || child.asset]}
                  assetUrls={assetUrls}
                />
              ))}
          </section>
        );
      })}
    </>
  );
}

function CompanyHeaderSection({ section, payload, assetUrl }: { section: LayoutSection; payload: FormattedInvoicePayload; assetUrl?: string }) {
  const dataset = asRecord(getDataset(payload, section.dataset));
  const fields = asRecord(section.fields);
  const documentPlacement = asRecord(asRecord(section.placement).document);
  return (
    <section
      className={`${styles["reports-v3-preview-section"]} ${styles["reports-v3-preview-company-header"]}`}
      style={{
        marginTop: cssLength(documentPlacement.previewMarginTop ?? documentPlacement.marginTop),
        marginBottom: cssLength(documentPlacement.previewMarginBottom ?? documentPlacement.marginBottom),
      }}
    >
      <div className={styles["reports-v3-preview-company-identity"]}>
        {assetUrl && <img className={styles["reports-v3-preview-company-logo"]} src={assetUrl} alt="" />}
        <div className={styles["reports-v3-preview-company-copy"]}>
          <div className={styles["reports-v3-preview-company-name"]}>{formatValue(getPath(dataset, stringOr(fields.name, "companyName")))}</div>
          <div className={styles["reports-v3-preview-company-email"]}>{formatValue(getPath(dataset, stringOr(fields.email, "companyEmail")))}</div>
        </div>
      </div>
      <div className={styles["reports-v3-preview-company-address"]}>
        {formatValue(getPath(dataset, stringOr(fields.address, "companyAddress")))}
      </div>
    </section>
  );
}

function ImageSection({ section, payload, assetUrl }: { section: LayoutSection; payload: FormattedInvoicePayload; assetUrl?: string }) {
  const size = asRecord(section.size);
  const resolvedAssetUrl = assetUrl || externalAssetUrl(payload, section.asset);
  if (!resolvedAssetUrl) return null;
  return (
    <section className={`${styles["reports-v3-preview-section"]} ${styles["reports-v3-preview-image"]}`} style={sectionBoxStyle(asRecord(section.styleTokens))}>
      <img
        src={resolvedAssetUrl}
        alt=""
        style={{
          maxWidth: cssLength(size.maxWidth),
          maxHeight: cssLength(size.maxHeight),
        }}
      />
    </section>
  );
}

function DividerSection({ section, layoutStyles }: { section: LayoutSection; layoutStyles: Record<string, unknown> }) {
  const style = getStyle(layoutStyles, section.style);
  return (
    <section className={styles["reports-v3-preview-section"]}>
      <hr
        className={styles["reports-v3-preview-divider"]}
        style={{
          borderTopColor: stringOr(style.color, "#23324a"),
          borderTopWidth: style.bottomBorder === "medium" ? 2 : 1,
        }}
      />
    </section>
  );
}

function KeyValueGridSection({
  section,
  payload,
  layoutStyles,
}: {
  section: LayoutSection;
  payload: FormattedInvoicePayload;
  layoutStyles: Record<string, unknown>;
}) {
  const dataset = getDataset(payload, section.dataset);
  const sectionStyle = getStyle(layoutStyles, section.style);
  const columns = positiveInteger(sectionStyle.columns, 3);
  const columnTemplate = cssGridColumnTemplate(sectionStyle.columnTemplate);
  const fieldPositions = keyValueFieldPositions(section.fields || [], columns);
  const renderField = (field: any, index: number, position?: { row: number }) => {
    const fieldStyle = { ...sectionStyle, ...getStyle(layoutStyles, field.style) };
    const value = formatValue(getPath(dataset, field.key), field.format);
    const hasLabel = Boolean(field.label);
    return (
      <div
        className={`${styles["reports-v3-preview-field"]} ${hasLabel ? "" : styles["reports-v3-preview-field--unlabeled"]}`}
        key={`${field.key || field.label || "field"}-${index}`}
        style={{
          ...textStyle(fieldStyle),
          marginTop: cssLength(fieldStyle.marginTop),
          gridRow: position?.row,
          gridTemplateColumns: fieldStyle.fieldLayout === "stacked"
            ? "1fr"
            : field.icon
              ? "max-content max-content minmax(0, 1fr)"
              : hasLabel
                ? "max-content minmax(0, 1fr)"
                : undefined,
          gap: cssGap(fieldStyle.fieldGapY, fieldStyle.fieldGapX, "6px"),
        }}
      >
        {field.icon && (
          <img
            className={styles["reports-v3-preview-field-icon"]}
            src={field.icon}
            alt=""
            aria-hidden="true"
            style={{
              width: cssLength(fieldStyle.iconSize),
              height: cssLength(fieldStyle.iconSize),
              filter: typeof fieldStyle.iconFilter === "string" ? fieldStyle.iconFilter : undefined,
            }}
          />
        )}
        {hasLabel && <span className={styles["reports-v3-preview-label"]} style={labelStyle(fieldStyle)}>{field.label}</span>}
        <span className={styles["reports-v3-preview-value"]} style={valueStyle(fieldStyle)}>{value}</span>
      </div>
    );
  };

  if (fieldPositions.length) {
    const fieldsByColumn = Array.from({ length: columns }, () => [] as Array<{ field: any; index: number; position: { column: number; row: number } }>);
    (section.fields || []).forEach((field: any, index: number) => {
      const position = fieldPositions[index];
      fieldsByColumn[(position?.column || 1) - 1]?.push({ field, index, position });
    });
    return (
      <section
        className={`${styles["reports-v3-preview-section"]} ${styles["reports-v3-preview-grid"]}`}
        style={{
          ...sectionBoxStyle(sectionStyle),
          gridTemplateColumns: columnTemplate || `repeat(${columns}, minmax(0, 1fr))`,
          columnGap: cssLength(sectionStyle.gapX) || "18px",
        }}
      >
        {fieldsByColumn.map((columnFields, columnIndex) => (
          <div
            key={`column-${columnIndex + 1}`}
            data-testid={`reports-v3-keyvalue-column-${columnIndex + 1}`}
            style={{
              display: "grid",
              alignContent: "start",
              rowGap: cssLengthOrZero(sectionStyle.gapY) || "8px",
              minWidth: 0,
            }}
          >
            {columnFields.map(({ field, index, position }) => renderField(field, index, { row: position.row }))}
          </div>
        ))}
      </section>
    );
  }

  return (
    <section
      className={`${styles["reports-v3-preview-section"]} ${styles["reports-v3-preview-grid"]}`}
      style={{
        ...sectionBoxStyle(sectionStyle),
        gridTemplateColumns: columnTemplate || `repeat(${columns}, minmax(0, 1fr))`,
        gap: cssGap(sectionStyle.gapY, sectionStyle.gapX, "8px 18px"),
      }}
    >
      {(section.fields || []).map((field: any, index: number) => renderField(field, index))}
    </section>
  );
}

function TextSection({
  section,
  payload,
  layoutStyles,
}: {
  section: LayoutSection;
  payload: FormattedInvoicePayload;
  layoutStyles: Record<string, unknown>;
}) {
  const style = getStyle(layoutStyles, section.style);
  return (
    <section className={styles["reports-v3-preview-section"]} style={sectionBoxStyle(style)}>
      <h2 className={styles["reports-v3-preview-title"]} style={textStyle(style)}>
        {resolveTemplate(payload, section.content || "")}
      </h2>
    </section>
  );
}

function TableSection({
  section,
  payload,
  layoutStyles,
}: {
  section: LayoutSection;
  payload: FormattedInvoicePayload;
  layoutStyles: Record<string, unknown>;
}) {
  const rows = tableRows(payload, section);
  const grouping = resolveGrouping(section);
  if (grouping?.key && hasGroupValues(rows, grouping.key) && hasGroupSubtotals(payload, section, grouping)) {
    const groups = groupRows(rows, grouping.key);
    const headerStyle = getStyle(layoutStyles, grouping.header?.style || "sectionTitle");
    return (
      <section className={styles["reports-v3-preview-section"]} style={sectionBoxStyle(getStyle(layoutStyles, section.sectionStyle))}>
        {section.title && section.placement?.document?.showTitle !== false && (
          <h2 className={styles["reports-v3-preview-title"]}>{section.title}</h2>
        )}
        {groups.map(([groupValue, groupRows]) => (
          <div key={`${section.id || section.dataset}-${groupValue}`}>
            <h3 className={styles["reports-v3-preview-title"]} style={textStyle(headerStyle)}>
              {resolveRowTemplate(grouping.header?.template || grouping.headerTemplate || `${grouping.key}: {{value}}`, {
                [grouping.key]: groupValue,
                value: groupValue,
              })}
            </h3>
            <LayoutTable
              section={section}
              payload={payload}
              rows={groupRows}
              layoutStyles={layoutStyles}
              suppressTitle
              footerRows={groupSubtotalFooterRows(payload, section, grouping, groupValue)}
            />
          </div>
        ))}
        {Array.isArray(section.footerRows) && section.footerRows.length > 0 && (
          <LayoutTable section={section} payload={payload} rows={[]} layoutStyles={layoutStyles} suppressTitle suppressHeader suppressBody footerRows={section.footerRows} />
        )}
      </section>
    );
  }
  const splitBy = resolveSplitBy(section);
  if (splitBy?.key) {
    const groups = groupRows(rows, splitBy.key);
    const headerStyle = getStyle(layoutStyles, splitBy.header?.style || "sectionTitle");
    return (
      <>
        {groups.map(([groupValue, groupRows]) => (
          <section className={styles["reports-v3-preview-section"]} key={`${section.id || section.dataset}-${groupValue}`}>
            <h2 className={styles["reports-v3-preview-title"]} style={textStyle(headerStyle)}>
              {resolveRowTemplate(splitBy.header?.template || `${section.title || splitBy.key}: {{value}}`, {
                [splitBy.key]: groupValue,
                value: groupValue,
              })}
            </h2>
            <LayoutTable section={section} payload={payload} rows={groupRows} layoutStyles={layoutStyles} suppressTitle />
          </section>
        ))}
      </>
    );
  }

  return (
    <section className={styles["reports-v3-preview-section"]} style={sectionBoxStyle(getStyle(layoutStyles, section.sectionStyle))}>
      <LayoutTable section={section} payload={payload} rows={rows} layoutStyles={layoutStyles} />
    </section>
  );
}

function LayoutTable({
  section,
  payload,
  rows,
  layoutStyles,
  suppressTitle = false,
  suppressHeader = false,
  suppressBody = false,
  footerRows,
}: {
  section: LayoutSection;
  payload: FormattedInvoicePayload;
  rows: Record<string, unknown>[];
  layoutStyles: Record<string, unknown>;
  suppressTitle?: boolean;
  suppressHeader?: boolean;
  suppressBody?: boolean;
  footerRows?: any[];
}) {
  const columns = physicalColumns(sectionColumns(section, payload));
  const tableStyle = getStyle(layoutStyles, section.style);
  const typography = tableTypography(tableStyle);
  const startCol = tableStartColumn(section);
  const physicalWidths = tablePhysicalWidths(section, columns, payload);
  const tableWidth = physicalWidths.reduce((sum, width) => sum + width, 0);
  const badges = tableSummaryBadges(payload, section);
  const tableBorder = tableStyle.border === false || tableStyle.border === "none"
    ? "none"
    : `1px solid ${stringOr(tableStyle.borderColor, "#808080")}`;
  const rowBorder = tableStyle.rowBorder === false || tableStyle.rowBorder === "none"
    ? "none"
    : `1px solid ${stringOr(tableStyle.rowBorderColor, "#e5e7eb")}`;

  return (
    <>
      {!suppressTitle && section.title && section.placement?.document?.showTitle !== false && (
        <h2 className={styles["reports-v3-preview-title"]}>{section.title}</h2>
      )}
      {badges.length > 0 && (
        <div className={styles["reports-v3-preview-summary-badges"]}>
          {badges.map((badge, index) => {
            const badgeStyle = statusStyle(tableStyle, badge.status);
            return (
              <span
                key={`${badge.status || "badge"}-${index}`}
                className={styles["reports-v3-preview-summary-badge"]}
                style={{
                  background: stringOr(badgeStyle.badgeFill, stringOr(badgeStyle.fill, "#f1f5f9")),
                  color: stringOr(badgeStyle.badgeColor, stringOr(badgeStyle.color, "#475569")),
                }}
              >
                {badge.count} {badge.label}
              </span>
            );
          })}
        </div>
      )}
      <div className={styles["reports-v3-preview-table-wrap"]}>
        <table
          className={styles["reports-v3-preview-table"]}
          style={{
            width: tableStyle.fullWidth === true ? "100%" : `${tableWidth}ch`,
            margin: tableStyle.fullWidth === true ? "0" : undefined,
          }}
        >
          <colgroup>
            {physicalWidths.map((width, index) => (
              <col key={index} style={{ width: `${width}ch` }} />
            ))}
          </colgroup>
          {!suppressHeader && <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={`${column.key || column.label}-${column.offset}`}
                  colSpan={column.span}
                  style={{
                    background: tableStyle.headerFill === false ? "transparent" : stringOr(tableStyle.headerFill, "#f0f5ff"),
                    border: tableBorder,
                    borderBottom: rowBorder,
                    color: stringOr(tableStyle.headerColor, "#000000"),
                    fontSize: typography.headerFontSize,
                    padding: `${typography.cellPaddingY}px ${typography.cellPaddingX}px`,
                    textAlign: alignValue(column.headerHorizontalAlign || column.horizontalAlign || column.align || tableStyle.headerHorizontalAlign, "center"),
                    verticalAlign: verticalAlignValue(tableStyle.headerVerticalAlign, "middle"),
                    whiteSpace: tableStyle.wrapHeaders === false ? "nowrap" : undefined,
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>}
          {!suppressBody && <tbody>
            {rows.map((row, rowIndex) => {
              const status = rowStyle(tableStyle, row);
              const stripeBackground = tableStyle.stripedRows === true && rowIndex % 2 === 1
                ? stringOr(tableStyle.stripeFill, "#f0f4f8")
                : undefined;
              const rowBackground = typeof status.fill === "string" ? status.fill : stripeBackground;
              return (
                <tr key={rowIndex}>
                  {columns.map((column, columnIndex) => (
                    <td
                      key={`${column.key || column.label}-${column.offset}`}
                      className={formatClass(column.format)}
                      colSpan={column.span}
                      style={{
                        background: rowBackground,
                        border: tableBorder,
                        borderBottom: rowBorder,
                        borderLeft: columnIndex === 0 && status.accent ? `3px solid ${status.accent}` : tableBorder,
                        color: stringOr(tableStyle.bodyColor, "#000000"),
                        fontSize: typography.bodyFontSize,
                        padding: `${typography.cellPaddingY}px ${typography.cellPaddingX}px`,
                        textAlign: alignValue(column.horizontalAlign || column.align || tableStyle.bodyHorizontalAlign, column.format === "money" || column.format === "number" ? "right" : "left"),
                        verticalAlign: verticalAlignValue(tableStyle.bodyVerticalAlign, "top"),
                        whiteSpace: column.wrap === true || tableStyle.bodyWrap === true ? undefined : "nowrap",
                      }}
                    >
                      {renderCellValue(row, column, tableStyle)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>}
          {Array.isArray(footerRows ?? section.footerRows) && (footerRows ?? section.footerRows).length > 0 && (
            <tfoot>
              {(footerRows ?? section.footerRows).map((footerRow: any) => {
                const cells = footerCells(footerRow, columns, startCol, payload, layoutStyles, tableStyle, rows);
                const marginTop = cells[0]?.style?.marginTop;
                return (
                  <React.Fragment key={footerRow.id || (footerRows ?? section.footerRows).indexOf(footerRow)}>
                    {marginTop != null && (
                      <tr aria-hidden="true">
                        <td colSpan={columns.reduce((sum, column) => sum + column.span, 0)} style={{ border: "none", height: cssLength(marginTop), padding: 0 }} />
                      </tr>
                    )}
                    <tr className={styles["reports-v3-preview-footer"]}>
                      {cells.map((cell, index) => (
                        <td
                          key={index}
                          colSpan={cell.span}
                          className={formatClass(cell.format)}
                          style={{
                            ...textStyle(cell.style),
                            background: typeof cell.style.fill === "string" ? cell.style.fill : undefined,
                            border: cell.style.pill === true ? "none" : tableBorder,
                            borderBottom: cell.style.pill === true ? "none" : rowBorder,
                            borderTopLeftRadius: cell.style.pill === true && index === 0 ? cssLength(cell.style.borderRadius) : undefined,
                            borderBottomLeftRadius: cell.style.pill === true && index === 0 ? cssLength(cell.style.borderRadius) : undefined,
                            borderTopRightRadius: cell.style.pill === true && index === cells.length - 1 ? cssLength(cell.style.borderRadius) : undefined,
                            borderBottomRightRadius: cell.style.pill === true && index === cells.length - 1 ? cssLength(cell.style.borderRadius) : undefined,
                            fontSize: cell.fontSize,
                            padding: `${cell.paddingY}px ${cell.paddingX}px`,
                            textAlign: alignValue(cell.style.horizontalAlign, cell.format === "money" || cell.format === "number" ? "right" : "left"),
                          }}
                        >
                          {cell.value}
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

function renderCellValue(row: Record<string, unknown>, column: TableColumn, tableStyle: LayoutStyle) {
  const value = formatValue(column.key ? getPath(row, column.key) : undefined, column.format);
  if (column.format !== "status") return value;
  const badgeStyle = statusStyle(tableStyle, value);
  return (
    <span
      className={styles["reports-v3-preview-status-badge"]}
      style={{
        background: stringOr(badgeStyle.badgeFill, stringOr(badgeStyle.fill, "#f1f5f9")),
        color: stringOr(badgeStyle.badgeColor, stringOr(badgeStyle.color, "#475569")),
      }}
    >
      {value}
    </span>
  );
}

function tableSummaryBadges(payload: FormattedInvoicePayload, section: LayoutSection): SummaryBadge[] {
  const config = asRecord(section.summaryBadges);
  const datasetName = typeof section.summaryBadges === "string" ? section.summaryBadges : typeof config.dataset === "string" ? config.dataset : "";
  if (!datasetName) return [];
  const dataset = getDataset(payload, datasetName);
  const rows = Array.isArray(dataset) ? dataset : Array.isArray(asRecord(dataset).rows) ? asRecord(dataset).rows : [];
  return rows
    .map((badge: any) => ({
      status: normalizeStatus(badge.status ?? badge.key ?? badge.label),
      count: badge.count ?? badge.value ?? "",
      label: badge.label ?? badge.status ?? "",
    }))
    .filter((badge: SummaryBadge) => Number(badge.count) > 0);
}

function rowStyle(tableStyle: LayoutStyle, row: Record<string, unknown>) {
  const field = typeof tableStyle.rowStyleField === "string" ? tableStyle.rowStyleField : undefined;
  return field ? statusStyle(tableStyle, getPath(row, field)) : {};
}

function statusStyle(tableStyle: LayoutStyle, status: unknown) {
  return getStyle(asRecord(tableStyle.rowStyles), normalizeStatus(status));
}

function normalizeStatus(value: unknown) {
  return String(value ?? "").trim().toLowerCase().replace(/\s+/g, "_");
}

function sectionColumns(section: LayoutSection, payload: FormattedInvoicePayload): TableColumn[] {
  const columns = [...(Array.isArray(section.columns) ? section.columns : [])];
  injectDynamicCustomColumns(columns, section, payload);
  if (section.dynamicExpenses !== true) return columns;

  const expenseColumns = dynamicExpenseColumns(section, payload);
  if (!expenseColumns.length) return columns;

  const insertBeforeKey = section.dynamicExpensesInsertBeforeKey || "total";
  const insertIndex = columns.findIndex((column) => column.key === insertBeforeKey);
  columns.splice(insertIndex >= 0 ? insertIndex : columns.length, 0, ...expenseColumns);
  return columns;
}

function injectDynamicCustomColumns(columns: TableColumn[], section: LayoutSection, payload: FormattedInvoicePayload) {
  const dataset = asRecord(getDataset(payload, section.dataset));
  const customColumns = Array.isArray(dataset.customColumns) ? dataset.customColumns : [];
  const existingKeys = new Set(columns.map((column) => column.key).filter(Boolean));
  const dynamicColumns = customColumns.flatMap((column: any): TableColumn[] => {
    const key = String(column?.key ?? column?.Key ?? "").trim();
    const label = String(column?.label ?? column?.Label ?? key).trim();
    if (!key || !label || existingKeys.has(key)) return [];
    existingKeys.add(key);
    return [{
      ...column,
      key,
      label,
      width: positiveNumber(column?.width ?? column?.Width, positiveNumber(section.dynamicCustomFieldWidth, 14)),
      wrap: column?.wrap ?? column?.Wrap ?? true,
      headerHorizontalAlign: column?.headerHorizontalAlign ?? column?.HeaderHorizontalAlign ?? "left",
      horizontalAlign: column?.horizontalAlign ?? column?.HorizontalAlign ?? "left",
    }];
  });
  if (!dynamicColumns.length) return;

  const insertBeforeKey = section.dynamicCustomFieldsInsertBeforeKey || "total";
  const insertIndex = columns.findIndex((column) => column.key === insertBeforeKey);
  columns.splice(insertIndex >= 0 ? insertIndex : columns.length, 0, ...dynamicColumns);
}

function dynamicExpenseColumns(section: LayoutSection, payload: FormattedInvoicePayload): TableColumn[] {
  const sourceName = section.dynamicExpensesSource || "expenseColumns";
  const expenseColumns = getDataset(payload, sourceName);
  const expenses = Array.isArray(expenseColumns) ? expenseColumns : [];
  const mileageRate = getPath(payload.datasets, section.dynamicExpensesMileageRatePath || "client.mileageRate");
  return expenses.flatMap((expense: any) => {
    const id = expense.id ?? expense.Id ?? expense.name ?? expense.Name;
    const name = String(expense.name ?? expense.Name ?? id ?? "").trim();
    if (!id || !name) return [];
    const inputType = String(expense.inputType ?? expense.InputType ?? "").toLowerCase();
    if (inputType === "integer") {
      const columns: TableColumn[] = [{
        key: `expenses.${id}.quantity`,
        label: name,
        format: "number",
        width: positiveNumber(section.dynamicExpenseQuantityWidth, 8),
        headerHorizontalAlign: "right",
        horizontalAlign: "right",
      }];
      if (mileageRate) {
        columns.push({
          key: `expenses.${id}.amount`,
          label: `${name} @${formatValue(mileageRate, "money")}/mi`,
          format: "money",
          width: positiveNumber(section.dynamicExpenseAmountWidth, 12),
          headerHorizontalAlign: "right",
          horizontalAlign: "right",
        });
      }
      return columns;
    }
    return [{
      key: `expenses.${id}.amount`,
      label: name,
      format: "money",
      width: positiveNumber(section.dynamicExpenseAmountWidth, 12),
      headerHorizontalAlign: "right",
      horizontalAlign: "right",
    }];
  });
}

function SignatureSection({ section, layoutStyles }: { section: LayoutSection; layoutStyles: Record<string, unknown> }) {
  const style = getStyle(layoutStyles, section.style);
  return (
    <section className={`${styles["reports-v3-preview-section"]} ${styles["reports-v3-preview-signatures"]}`} style={textStyle(style)}>
      {(section.fields || []).map((field: string) => (
        <div className={styles["reports-v3-preview-signature"]} key={field}>
          <span>{field}</span>
          <span className={styles["reports-v3-preview-line"]} />
        </div>
      ))}
    </section>
  );
}

function CustomFieldsSection({
  section,
  payload,
  layoutStyles,
}: {
  section: LayoutSection;
  payload: FormattedInvoicePayload;
  layoutStyles: Record<string, unknown>;
}) {
  const rows = customFieldRows(payload, section);
  if (rows.length === 0) return null;
  const style = getStyle(layoutStyles, section.style);
  const columns = positiveInteger(style.columns, positiveInteger(section.columns, 2));
  return (
    <section
      className={`${styles["reports-v3-preview-section"]} ${styles["reports-v3-preview-grid"]}`}
      style={{
        ...sectionBoxStyle(style),
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: cssGap(style.gapY, style.gapX, "8px 18px"),
      }}
    >
      {section.title && <h2 className={styles["reports-v3-preview-title"]} style={{ gridColumn: "1 / -1", ...textStyle(style) }}>{section.title}</h2>}
      {rows.map((field, index) => (
        <div
          key={`${field.id ?? field.label ?? "field"}-${index}`}
          className={styles["reports-v3-preview-field"]}
          style={{ ...textStyle(style), gridTemplateColumns: "1fr", gap: cssGap(style.fieldGapY, style.fieldGapX, "4px") }}
        >
          <span className={styles["reports-v3-preview-label"]} style={labelStyle(style)}>{String(field.label ?? "")}</span>
          <span className={styles["reports-v3-preview-value"]} style={valueStyle(style)}>{String(field.value ?? "")}</span>
        </div>
      ))}
    </section>
  );
}

function previewSections(payload: FormattedInvoicePayload): LayoutSection[] {
  const layout = asRecord(payload.layout);
  const sections = Array.isArray(layout.sections) ? layout.sections : [];
  return sections.filter((section) => Array.isArray(section?.outputs) && section.outputs.includes("preview"));
}

function assetSections(sections: LayoutSection[]): LayoutSection[] {
  return sections.flatMap((section) => (
    section.type === "repeat"
      ? [section, ...assetSections(repeatSections(section))]
      : [section]
  ));
}

function repeatRows(payload: FormattedInvoicePayload, section: LayoutSection): Record<string, unknown>[] {
  const dataset = getDataset(payload, section.dataset);
  return Array.isArray(dataset) ? dataset.filter(isRecord) : [];
}

function repeatSections(section: LayoutSection): LayoutSection[] {
  return Array.isArray(section.sections) ? section.sections.filter(isRecord) : [];
}

function isDuplicateSummaryCustomFields(payload: FormattedInvoicePayload, section: LayoutSection): boolean {
  if (section.type !== "customFields" || section.dataset !== "invoiceCustomFields.summary") return false;
  const prospectSummary = asRecord(getDataset(payload, "prospectSummary"));
  return Array.isArray(prospectSummary.customColumns) && prospectSummary.customColumns.length > 0;
}

function createScopedPayload(payload: FormattedInvoicePayload, scope: Record<string, unknown>): FormattedInvoicePayload {
  return {
    ...payload,
    datasets: {
      ...(payload.datasets || {}),
      ...scope,
    },
  };
}

function tableRows(payload: FormattedInvoicePayload, section: LayoutSection): Record<string, unknown>[] {
  const dataset = getDataset(payload, section.dataset);
  if (Array.isArray(dataset)) return dataset.filter(isRecord);
  const record = asRecord(dataset);
  return Array.isArray(record.rows) ? record.rows.filter(isRecord) : [];
}

function getDataset(payload: FormattedInvoicePayload, name?: string): unknown {
  if (!name) return undefined;
  return payload.datasets?.[name] ?? payload.datasets?.[capitalize(name)] ?? getPath(payload.datasets, name);
}

function customFieldRows(payload: FormattedInvoicePayload, section: LayoutSection): Array<Record<string, unknown>> {
  const dataset = getDataset(payload, section.dataset);
  const rows = Array.isArray(dataset) ? dataset : [];
  return rows
    .filter((field) => isRecord(field) && String(field.label ?? "").trim())
    .sort((a: any, b: any) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0));
}

function resolveGrouping(section: LayoutSection) {
  const grouping = asRecord(section.grouping);
  const modes = asRecord(grouping.mode);
  const documentMode = modes.preview ?? modes.document ?? asRecord(grouping.modes).preview;
  if (!grouping.key || documentMode !== "sections") return null;
  return grouping;
}

function groupSubtotalFooterRows(payload: FormattedInvoicePayload, section: LayoutSection, grouping: Record<string, any>, groupValue: string) {
  const subtotal = findGroupSubtotal(payload, section, grouping, groupValue);
  if (!subtotal) return [];
  const columns = sectionColumns(section, payload);
  const totalCells = columns.map((column, index) => {
    const value = index === 0
      ? `${groupValue} Total`
      : getPath(subtotal.values ?? subtotal, column.key);
    return {
      range: `${numberToColumnName(tableStartColumn(section) + index)}:${numberToColumnName(tableStartColumn(section) + index)}`,
      rawValue: value,
      format: column.format,
      style: index === 0 ? "subtotalLabelCell" : "subtotalValueCell",
    };
  });
  return [{ id: `${groupValue}-subtotal`, cells: totalCells }];
}

function findGroupSubtotal(payload: FormattedInvoicePayload, section: LayoutSection, grouping: Record<string, any>, groupValue: string) {
  const dataset = asRecord(getDataset(payload, section.dataset));
  const totals = Array.isArray(dataset[grouping.subtotalSource]) ? dataset[grouping.subtotalSource] : [];
  return totals.find((total: any) => String(getPath(total, grouping.key) ?? total.county ?? "") === groupValue);
}

function resolveAsset(payload: FormattedInvoicePayload, assetKey?: string): ReportAssetV3 | null {
  if (!assetKey) return null;
  return (payload.assets?.[assetKey] || payload.assets?.[assetKey.replace(".", "")] || payload.assets?.companyLogo || null) as ReportAssetV3 | null;
}

function externalAssetUrl(payload: FormattedInvoicePayload, assetKey?: string): string | undefined {
  const asset = resolveAsset(payload, assetKey);
  return asset?.available && asset.url && isExternalAssetUrl(asset.url) ? asset.url : undefined;
}

function resolveSplitBy(section: LayoutSection): any {
  return section.placement?.document?.splitBy || section.placement?.excel?.splitBy || null;
}

function physicalColumns(columns: TableColumn[]) {
  let offset = 0;
  return columns.map((column) => {
    const span = positiveInteger(column.bodyColSpan || column.colSpan, 1);
    const physical = { ...column, span, offset };
    offset += span;
    return physical;
  });
}

function tablePhysicalWidths(section: LayoutSection, columns: ReturnType<typeof physicalColumns>, payload: FormattedInvoicePayload): number[] {
  const sheetWidths = sheetColumnWidths(payload, section.placement?.excel?.sheet);
  const startCol = tableStartColumn(section);
  const widths: number[] = [];
  columns.forEach((column) => {
    for (let offset = 0; offset < column.span; offset += 1) {
      const physicalIndex = startCol + column.offset + offset;
      widths.push(sheetWidths.get(physicalIndex) || positiveNumber(column.width, 12) || 12);
    }
  });
  return widths;
}

function sheetColumnWidths(payload: FormattedInvoicePayload, sheetName?: string): Map<number, number> {
  const layout = asRecord(payload.layout);
  const excel = asRecord(asRecord(layout.outputs).excel);
  const sheets = Array.isArray(excel.sheets) ? excel.sheets : [];
  const sheet = sheets.find((candidate) => asRecord(candidate).name === sheetName) || sheets.find((candidate) => asRecord(candidate).name === "Invoice");
  const widths = new Map<number, number>();
  for (const column of asRecord(sheet).columns || []) {
    const record = asRecord(column);
    const index = Number(record.index);
    const width = Number(record.width);
    if (Number.isInteger(index) && index > 0 && Number.isFinite(width) && width > 0) {
      widths.set(index, width);
    }
  }
  return widths;
}

function footerCells(
  footerRow: any,
  columns: ReturnType<typeof physicalColumns>,
  startCol: number,
  payload: FormattedInvoicePayload,
  layoutStyles: Record<string, unknown>,
  tableStyle: LayoutStyle,
  rows: Record<string, unknown>[] = [],
) {
  if (footerRow.fromColumns === true) {
    const rowStyle = getStyle(layoutStyles, footerRow.style);
    const totals = getDataset(payload, footerRow.dataset || footerRow.totalsDataset);
    const labelColumnKey = footerRow.labelColumnKey || columns[0]?.key;
    return columns.map((column) => {
      const isLabelColumn = column.key === labelColumnKey;
      const cellStyle = getStyle(layoutStyles, footerRow.cellStyle);
      const mergedStyle = { ...rowStyle, ...cellStyle };
      const typography = tableTypography({ ...tableStyle, ...mergedStyle });
      return {
        span: column.span,
        value: isLabelColumn
          ? String(footerRow.label || "TOTALS")
          : formatValue(footerRow.aggregateRows === true ? aggregateColumnRows(rows, column) : getPath(totals, column.key), column.format),
        format: column.format,
        style: mergedStyle,
        fontSize: typography.footerFontSize,
        paddingX: typography.cellPaddingX,
        paddingY: typography.cellPaddingY,
      };
    });
  }

  const physicalWidth = columns.reduce((sum, column) => sum + column.span, 0);
  const rowStyle = getStyle(layoutStyles, footerRow.style);
  const baseTypography = tableTypography({ ...tableStyle, ...rowStyle });
  const cells = [...(footerRow.cells || [])]
    .map((cell: any) => ({ cell, range: parseFooterRange(cell.range) }))
    .filter((entry) => entry.range)
    .map((entry) => ({
      ...entry,
      start: Math.max(0, entry.range!.startCol - startCol),
      end: Math.min(physicalWidth - 1, entry.range!.endCol - startCol),
    }))
    .filter((entry) => entry.start <= entry.end)
    .sort((a, b) => a.start - b.start);

  const rendered: Array<{
    span: number;
    value: string;
    format?: string;
    style: LayoutStyle;
    fontSize: number;
    paddingX: number;
    paddingY: number;
  }> = [];
  let cursor = 0;
  for (const entry of cells) {
    if (entry.start > cursor) {
      rendered.push({
        span: entry.start - cursor,
        value: "",
        style: rowStyle,
        fontSize: baseTypography.footerFontSize,
        paddingX: baseTypography.cellPaddingX,
        paddingY: baseTypography.cellPaddingY,
      });
    }
    const cellStyle = getStyle(layoutStyles, entry.cell.style);
    const mergedStyle = { ...rowStyle, ...cellStyle };
    const typography = tableTypography({ ...tableStyle, ...mergedStyle });
    rendered.push({
      span: entry.end - entry.start + 1,
      value: formatValue(entry.cell.rawValue ?? resolveTemplate(payload, entry.cell.value, entry.cell.fallback), entry.cell.format),
      format: entry.cell.format,
      style: mergedStyle,
      fontSize: typography.footerFontSize,
      paddingX: typography.cellPaddingX,
      paddingY: typography.cellPaddingY,
    });
    cursor = entry.end + 1;
  }
  if (cursor < physicalWidth) {
    rendered.push({
      span: physicalWidth - cursor,
      value: "",
      style: rowStyle,
      fontSize: baseTypography.footerFontSize,
      paddingX: baseTypography.cellPaddingX,
      paddingY: baseTypography.cellPaddingY,
    });
  }
  return rendered;
}

function aggregateColumnRows(rows: Record<string, unknown>[], column: ReturnType<typeof physicalColumns>[number]): unknown {
  const aggregate = (column as TableColumn).aggregate || ((column.format === "money" || column.format === "number") ? "sum" : undefined);
  if (!column.key || aggregate !== "sum") return "";
  return rows.reduce((sum, row) => {
    const value = Number(getPath(row, column.key) ?? 0);
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);
}

function tableTypography(style: LayoutStyle) {
  const fontSize = positiveNumber(style.fontSize, 10);
  return {
    headerFontSize: positiveNumber(style.headerFontSize, fontSize),
    bodyFontSize: positiveNumber(style.bodyFontSize, fontSize),
    footerFontSize: positiveNumber(style.footerFontSize, fontSize),
    cellPaddingX: nonNegativeNumber(style.cellPaddingX, 3),
    cellPaddingY: nonNegativeNumber(style.cellPaddingY, 5),
  };
}

function textStyle(style: LayoutStyle): React.CSSProperties {
  return {
    color: typeof style.color === "string" ? style.color : undefined,
    fontSize: positiveNumber(style.fontSize, undefined),
    fontWeight: style.bold ? 700 : undefined,
    lineHeight: positiveNumber(style.lineHeight, undefined),
  };
}

function sectionBoxStyle(style: LayoutStyle): React.CSSProperties {
  const backgroundImage = typeof style.backgroundImage === "string" ? `url("${style.backgroundImage}")` : undefined;
  return {
    backgroundColor: typeof style.backgroundColor === "string" ? style.backgroundColor : undefined,
    backgroundImage,
    backgroundSize: typeof style.backgroundSize === "string" ? style.backgroundSize : undefined,
    backgroundPosition: typeof style.backgroundPosition === "string" ? style.backgroundPosition : undefined,
    backgroundRepeat: typeof style.backgroundRepeat === "string" ? style.backgroundRepeat : undefined,
    border: typeof style.borderColor === "string" ? `1px solid ${style.borderColor}` : undefined,
    borderRadius: cssLength(style.borderRadius),
    marginTop: cssLength(style.marginTop),
    marginBottom: cssLength(style.marginBottom),
    padding: cssPadding(style),
    overflow: style.overflow === "hidden" ? "hidden" : undefined,
  };
}

function labelStyle(style: LayoutStyle): React.CSSProperties {
  return {
    color: typeof style.labelColor === "string" ? style.labelColor : undefined,
    fontWeight: style.labelBold === false ? 400 : undefined,
    minWidth: cssLength(style.labelMinWidth),
    whiteSpace: style.labelNoWrap === true ? "nowrap" : undefined,
  };
}

function valueStyle(style: LayoutStyle): React.CSSProperties {
  return {
    color: typeof style.valueColor === "string" ? style.valueColor : undefined,
    fontWeight: style.valueBold === false ? 400 : undefined,
    lineHeight: positiveNumber(style.lineHeight, undefined),
    paddingRight: cssLength(style.paddingRight),
    textAlign: alignValue(style.valueHorizontalAlign, "left"),
  };
}

function formatClass(format?: string) {
  if (format === "money") return styles["reports-v3-preview-money"];
  if (format === "number") return styles["reports-v3-preview-number"];
  return undefined;
}

function formatValue(value: unknown, format?: string): string {
  if (value === undefined || value === null) return "";
  if (format === "money") {
    const numeric = Number(value);
    return Number.isFinite(numeric)
      ? numeric.toLocaleString("en-US", { style: "currency", currency: "USD" })
      : String(value);
  }
  if (format === "number") {
    const numeric = Number(value);
    return Number.isFinite(numeric)
      ? numeric.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
      : String(value);
  }
  if (format === "date") {
    return formatDate(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function formatDate(value: unknown): string {
  if (typeof value !== "string") return String(value);
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value;
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${month}/${day}/${year}`;
}

function resolveTemplate(payload: FormattedInvoicePayload, template?: string, fallback = ""): string {
  const source = template || fallback || "";
  const resolved = String(source).replace(/\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g, (_, path) => {
    const value = getPath(payload.datasets || {}, path);
    return value === undefined || value === null ? "" : String(value);
  });
  if (!resolved && fallback && fallback !== source) return resolveTemplate(payload, fallback);
  return resolved;
}

function resolveRowTemplate(template: string, row: Record<string, unknown>): string {
  return String(template || "").replace(/\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g, (_, path) => {
    const value = getPath(row, path);
    return value === undefined || value === null ? "" : String(value);
  });
}

function groupRows(rows: Record<string, unknown>[], key: string): Array<[string, Record<string, unknown>[]]> {
  const groups = new Map<string, Record<string, unknown>[]>();
  rows.forEach((row) => {
    const value = String(getPath(row, key) || "Unassigned");
    if (!groups.has(value)) groups.set(value, []);
    groups.get(value)!.push(row);
  });
  return Array.from(groups.entries());
}

function hasGroupValues(rows: Record<string, unknown>[], key: string): boolean {
  return rows.some((row) => getPath(row, key) !== undefined && getPath(row, key) !== null && String(getPath(row, key)).trim() !== "");
}

function hasGroupSubtotals(payload: FormattedInvoicePayload, section: LayoutSection, grouping: Record<string, any>): boolean {
  const dataset = asRecord(getDataset(payload, section.dataset));
  return Array.isArray(dataset[grouping.subtotalSource]) && (dataset[grouping.subtotalSource] as unknown[]).length > 0;
}

function tableStartColumn(section: LayoutSection): number {
  const placement = section.placement?.excel || {};
  const cell = placement.startCell || placement.startCellFallback || "A1";
  return parseCell(cell)?.col || 1;
}

function parseFooterRange(range?: string): { startCol: number; endCol: number } | null {
  if (!range) return null;
  const [start, end] = String(range).split(":");
  const startCol = columnNameToNumber(start);
  const endCol = columnNameToNumber(end || start);
  return startCol && endCol ? { startCol, endCol } : null;
}

function parseCell(value: string): { col: number; row: number } | null {
  const match = String(value).match(/^([A-Z]+)(\d+)$/i);
  if (!match) return null;
  return { col: columnNameToNumber(match[1]), row: Number(match[2]) };
}

function columnNameToNumber(name: string): number {
  return String(name).replace(/[^A-Z]/gi, "").toUpperCase().split("").reduce((sum, char) => (
    sum * 26 + char.charCodeAt(0) - 64
  ), 0);
}

function numberToColumnName(value: number): string {
  let current = value;
  let name = "";
  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }
  return name || "A";
}

function getPath(source: unknown, path?: string): unknown {
  if (!source || !path) return undefined;
  return String(path).split(".").reduce<unknown>((current, part) => (
    current && typeof current === "object" ? (current as Record<string, unknown>)[part] : undefined
  ), source);
}

function getStyle(stylesRecord: Record<string, unknown>, name?: string): LayoutStyle {
  return asRecord(name ? stylesRecord[name] : undefined);
}

function asRecord(value: unknown): Record<string, any> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, any>
    : {};
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function positiveInteger(value: unknown, fallback: number): number {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : fallback;
}

function positiveNumber(value: unknown, fallback: number): number;
function positiveNumber(value: unknown, fallback: undefined): number | undefined;
function positiveNumber(value: unknown, fallback: number | undefined): number | undefined {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
}

function nonNegativeNumber(value: unknown, fallback: number): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : fallback;
}

function cssLength(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? `${numeric}px` : undefined;
}

function cssLengthOrZero(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? `${numeric}px` : undefined;
}

function cssPadding(style: LayoutStyle): string | undefined {
  const top = cssLength(style.paddingTop ?? style.paddingY);
  const right = cssLength(style.paddingRight ?? style.paddingX);
  const bottom = cssLength(style.paddingBottom ?? style.paddingY);
  const left = cssLength(style.paddingLeft ?? style.paddingX);
  if (!top && !right && !bottom && !left) return undefined;
  return `${top || "0"} ${right || "0"} ${bottom || "0"} ${left || "0"}`;
}

function cssGap(rowGap: unknown, columnGap: unknown, fallback: string): string {
  const row = cssLength(rowGap);
  const column = cssLength(columnGap);
  if (!row && !column) return fallback;
  return `${row || "0"} ${column || row || "0"}`;
}

function cssGridColumnTemplate(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (!Array.isArray(value)) return undefined;
  const parts = value
    .map((part) => typeof part === "number" ? `${part}fr` : String(part || "").trim())
    .filter(Boolean);
  return parts.length ? parts.join(" ") : undefined;
}

function keyValueFieldPositions(fields: any[], columns: number) {
  if (!fields.some((field) => Number.isFinite(Number(field?.column)))) return [];
  const rowByColumn = Array(columns).fill(0);
  return fields.map((field) => {
    const column = Math.min(columns, Math.max(1, Math.floor(Number(field?.column) || 1)));
    const explicitRow = Number(field?.row);
    const row = Number.isFinite(explicitRow) && explicitRow > 0
      ? Math.floor(explicitRow)
      : rowByColumn[column - 1] + 1;
    rowByColumn[column - 1] = Math.max(rowByColumn[column - 1], row);
    return { column, row };
  });
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

function alignValue(value: unknown, fallback: React.CSSProperties["textAlign"]) {
  return typeof value === "string" ? value as React.CSSProperties["textAlign"] : fallback;
}

function verticalAlignValue(value: unknown, fallback: React.CSSProperties["verticalAlign"]) {
  return typeof value === "string" ? value as React.CSSProperties["verticalAlign"] : fallback;
}

function capitalize(value = "") {
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
}

function isExternalAssetUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function normalizeAssetPath(pathOrUrl: string): string {
  try {
    const url = new URL(pathOrUrl, window.location.origin);
    return url.pathname.replace(/^\/api\/?/i, "");
  } catch {
    return pathOrUrl.replace(/^\/api\/?/i, "");
  }
}
